import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { fetchQueue, fetchStartedToday, subscribeToQueue } from '../services/queue';
import type { QueueEntry, QueueOrderPolicy, StartedEntry } from '../types/database';

interface Ctx {
  /** The location these rows belong to (infra.rooms.location). Exposed because
   *  consumers that need other per-location data (the booking outlook) should
   *  not have to re-derive it from the room. */
  locationId: string | null;
  /** Live queue at the current location, ordered by visual position. Parked
   *  ("even weg") rows stay in place with parked=true. */
  entries: QueueEntry[];
  /** position_only (pure check-in order, default) or slot_first. */
  orderPolicy: QueueOrderPolicy;
  /** ETA inputs the RPC used (informational). */
  stations: number;
  /** Minutes inside the headset; cycleMinutes = runMinutes + turnaround. */
  runMinutes: number;
  cycleMinutes: number;
  /** First non-parked, NON-CLAIMED entry — the visitor a launch pops next. */
  headEntry: QueueEntry | null;
  /** Take this entry out of head selection while a launch is in flight for it,
   *  so a second PC started moments later pops the NEXT person instead of the
   *  same one. The dequeue only happens once the agent confirms the launch,
   *  which is seconds later, and until then the queue still lists them. */
  claimEntry: (queueEntryId: string) => void;
  /** Launch failed: put them back at the head. Claims also expire on their own
   *  and are dropped once the entry actually leaves the queue. */
  releaseEntry: (queueEntryId: string) => void;
  /** null = head; otherwise the queue_entry_id chosen via QueuePickerModal. */
  selectedDequeueEntryId: string | null;
  /** Resolved selection — selected entry if any, else head. */
  selectedEntry: QueueEntry | null;
  /** When true (default), a successful experience launch dequeues the selected entry. */
  dequeueOnLaunch: boolean;
  setSelected: (queueEntryId: string | null) => void;
  setDequeueOnLaunch: (v: boolean) => void;
  /** Force a refetch (e.g. after a manual dequeue). */
  refresh: () => void;
  /** Loading state for first fetch. */
  loading: boolean;
  /** Today's started rows at the location, newest event first. */
  startedToday: StartedEntry[];
  /** Newest started row per PC (infra.pc_instances.id) — "who plays here". */
  startedByPc: ReadonlyMap<string, StartedEntry>;
  /** The started row with the most recent event: what fn_undo_last_dequeue
   *  would most likely restore. Age check is the caller's (5-minute window). */
  latestDequeue: StartedEntry | null;
}

/** How long a launch claim survives without being confirmed or released.
 *  Longer than any real launch, short enough that a wedged tab frees the
 *  queue on its own. */
const CLAIM_TTL_MS = 90_000;

const QueueDequeueContext = createContext<Ctx | null>(null);

interface ProviderProps {
  /** Pulled from the active room's `location` field (infra.rooms.location). */
  locationId: string | null;
  children: ReactNode;
}

export function QueueDequeueProvider({ locationId, children }: ProviderProps) {
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [orderPolicy, setOrderPolicy] = useState<QueueOrderPolicy>('position_only');
  const [stations, setStations] = useState(1);
  const [runMinutes, setRunMinutes] = useState(20);
  const [cycleMinutes, setCycleMinutes] = useState(25);
  const [startedToday, setStartedToday] = useState<StartedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDequeueEntryId, setSelected] = useState<string | null>(null);
  const [dequeueOnLaunch, setDequeueOnLaunch] = useState(true);
  const [claimed, setClaimed] = useState<ReadonlySet<string>>(new Set());

  const load = useCallback(async () => {
    if (!locationId) {
      setEntries([]);
      setStartedToday([]);
      setLoading(false);
      return;
    }
    // Queue + started-today together: one realtime event (a dequeue moves a
    // row from one list to the other) refreshes both, and a failure of one
    // never blanks the other. Don't blow away the previous snapshot on a
    // transient fetch failure — the operator keeps the last good view.
    const [q, s] = await Promise.allSettled([fetchQueue(locationId), fetchStartedToday(locationId)]);
    if (q.status === 'fulfilled') {
      setEntries(q.value.entries);
      setOrderPolicy(q.value.orderPolicy);
      setStations(q.value.stations);
      setRunMinutes(q.value.runMinutes);
      setCycleMinutes(q.value.cycleMinutes);
    } else {
      console.error('fetchQueue failed', q.reason);
    }
    if (s.status === 'fulfilled') {
      setStartedToday(s.value);
    } else {
      console.error('fetchStartedToday failed', s.reason);
    }
    setLoading(false);
  }, [locationId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (!locationId) return;
    // Debounce a burst of postgres_changes events (one per-person enqueue
    // inserts N rows in one txn → N events) into a single refetch.
    let t: ReturnType<typeof setTimeout> | null = null;
    const unsub = subscribeToQueue(locationId, () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { void load(); }, 300);
    });
    return () => {
      if (t) clearTimeout(t);
      unsub();
    };
  }, [locationId, load]);

  // 60s safety poll for websocket gaps.
  useEffect(() => {
    if (!locationId) return;
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [locationId, load]);

  // Clear the override when the selected entry leaves the queue (got
  // dequeued by another operator, removed from admin, etc.).
  useEffect(() => {
    if (
      selectedDequeueEntryId &&
      !entries.some((e) => e.queue_entry_id === selectedDequeueEntryId)
    ) {
      setSelected(null);
    }
  }, [entries, selectedDequeueEntryId]);

  // --- Launch claims. A tile resolves who it is popping when the operator
  // CLICKS, but only pops them after the agent confirms, which can be ten
  // seconds later. Without a claim a second tile started in that window reads
  // the same head and both launches target one person: on 2026-09-11 that put
  // two visitors on two PCs with only one of them dequeued. The claim is
  // local (one operator, one screen) and deliberately not persisted; the
  // database refusal added in migration 20260911175211 is the real backstop.
  const claimTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const claimEntry = useCallback((id: string) => {
    setClaimed((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    // A launch that neither succeeds nor throws (tab closed mid-flight, agent
    // never answers) must not strand the queue behind a claim forever.
    const existing = claimTimers.current.get(id);
    if (existing) clearTimeout(existing);
    claimTimers.current.set(
      id,
      setTimeout(() => {
        claimTimers.current.delete(id);
        setClaimed((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, CLAIM_TTL_MS),
    );
  }, []);
  const releaseEntry = useCallback((id: string) => {
    const t = claimTimers.current.get(id);
    if (t) {
      clearTimeout(t);
      claimTimers.current.delete(id);
    }
    setClaimed((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);
  useEffect(() => () => {
    for (const t of claimTimers.current.values()) clearTimeout(t);
    claimTimers.current.clear();
  }, []);

  // A claimed entry that has left the queue was popped successfully: the claim
  // did its job and holding it would only leak.
  useEffect(() => {
    if (claimed.size === 0) return;
    const live = new Set(entries.map((e) => e.queue_entry_id));
    for (const id of claimed) if (!live.has(id)) releaseEntry(id);
  }, [entries, claimed, releaseEntry]);

  const headEntry = useMemo(
    () => entries.find((e) => !e.parked && !claimed.has(e.queue_entry_id)) ?? null,
    [entries, claimed],
  );
  const selectedEntry = useMemo(() => {
    if (selectedDequeueEntryId) {
      return entries.find((e) => e.queue_entry_id === selectedDequeueEntryId) ?? null;
    }
    return headEntry;
  }, [entries, selectedDequeueEntryId, headEntry]);

  // startedToday is newest-first, so the first row seen per PC wins.
  const startedByPc = useMemo(() => {
    const m = new Map<string, StartedEntry>();
    for (const s of startedToday) {
      if (s.started_pc_id && !m.has(s.started_pc_id)) m.set(s.started_pc_id, s);
    }
    return m;
  }, [startedToday]);
  const latestDequeue = startedToday[0] ?? null;

  const value: Ctx = {
    locationId,
    entries,
    orderPolicy,
    stations,
    runMinutes,
    cycleMinutes,
    headEntry,
    claimEntry,
    releaseEntry,
    selectedDequeueEntryId,
    selectedEntry,
    dequeueOnLaunch,
    setSelected,
    setDequeueOnLaunch,
    refresh: () => void load(),
    loading,
    startedToday,
    startedByPc,
    latestDequeue,
  };

  return (
    <QueueDequeueContext.Provider value={value}>
      {children}
    </QueueDequeueContext.Provider>
  );
}

export function useQueueDequeue(): Ctx {
  const v = useContext(QueueDequeueContext);
  if (!v) {
    throw new Error('useQueueDequeue must be used inside <QueueDequeueProvider>');
  }
  return v;
}

// Optional accessor that returns null when there's no provider — useful for
// PCTile, which renders on both auth-bypassed pages (QueueDisplay) and the
// normal Dashboard.
export function useQueueDequeueOptional(): Ctx | null {
  return useContext(QueueDequeueContext);
}
